'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import React from 'react'
import { useInView } from 'react-intersection-observer'

import { getPageFromSearchParam, updatePageQueryParam } from '@/shared/lib/pagination/client'
import { getPosts } from '@/shared/newsdb'
import { useI18n } from '@/shared/providers'

import { LoadingSpinner } from './loading-spinner'
import { PostMemoized } from './post'

function usePaginatedPosts(tag: string) {
    return useInfiniteQuery({
        queryKey: ['feed', tag],
        queryFn: ({ pageParam }) => getPosts({ page: pageParam, tag }),
        retry: 2, // Note: 2 retry attempts before failing, back-off delay is gradually applied to each retry attempt
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            if (lastPage.length === 0) {
                return
            }
            return lastPageParam + 1
        },
        staleTime: 1000 * 60 * 15, // 15 minutes
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    })
}

export function FeedInfinite({ selectedTag }: { selectedTag: string }) {
    const { ref, inView } = useInView({
        rootMargin: '300px 0px',
    })

    const searchParams = useSearchParams()

    const { data, fetchNextPage, hasNextPage, status, isFetching } = usePaginatedPosts(selectedTag)

    // biome-ignore lint/correctness/useExhaustiveDependencies: The dependencies are correct
    React.useEffect(() => {
        // NOTE: To ensure a seamless querying process without conflicts, it's highly recommended to verify that the query is not in an isFetching state
        if (inView && hasNextPage && !isFetching) {
            fetchNextPage().then(() => {
                const pageIndex = getPageFromSearchParam(searchParams.get('page'))
                updatePageQueryParam(pageIndex + 1)
            })
        }
    }, [inView, fetchNextPage, hasNextPage])

    if (status === 'pending') {
        return <LoadingSpinner />
    }

    return (
        <React.Fragment>
            {data?.pages.flat().map((post) => (
                <PostMemoized key={post.id} post={post} selectedTag={selectedTag} />
            ))}

            {status === 'error' ? <ErrorMessage /> : null}

            {isFetching ? <LoadingSpinner /> : null}

            <div ref={ref} />
        </React.Fragment>
    )
}

function ErrorMessage() {
    const { i18n } = useI18n()

    return (
        <div className="d-flex justify-content-center mt-3 mb-3">
            <p>
                {i18n.TEXT_TOAST_FAIL}. {i18n.TEXT_TOAST_TRY_LATER}
            </p>
        </div>
    )
}
